import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Paper } from '@mui/material';

/** Material UI Icons. */
import QuizIcon from '@mui/icons-material/Quiz';
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { callAPI } from 'shared/api/callApi';
import ThemeButton from 'shared/ui/Button';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import CircularLoading from 'shared/ui/CircularLoading';
import { MainDatagrid } from 'shared/ui/Datagrid';
import DownloadBlobAsZip from 'shared/lib/downloadBlobAsZip';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { PageHeader } from 'shared/ui/HeaderIcon';
import useUserRole from 'shared/hooks/useUserRole';

import getGuidelineCol from './lib/GuidelineDef';
import GuidelineDialog from './components/GuidelineDialog';
import GuidelineDetailPanel from './components/GuidelineDetailPanel';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

export default function Guideline() {
    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Get a current userRole array. */
    const userRole = useUserRole();

    /** State for guideline data grid row. */
    const [guideLineRow, setGuideLineRow] = useState([]);
    /** State for activating new update dialog. */
    const [newOpen, setNewOpen] = useState(false);
    /** State for storing the selected row ID, used when the dialog is opened.  */
    const [selectedRowId, setSelectedRowId] = useState();
    /** Loading state for initial page load. */
    const [initialLoading, setInitialLoading] = useState(false);
    /** Loading state for user actions (delete, download). */
    const [actionLoading, setActionLoading] = useState(false);
    /** State for triggering the GET API for updating the data grid's data. */
    const [rowUpdateTrigger, setRowUpdateTrigger] = useState(false);
    /** State for storing the current page number and page size of the data grid. */
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    /** State for displaying the total number of data entries in the data grid. */
    const [rowCount, setRowCount] = useState(100);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Guides & FAQs | Operation System';
    }, []);

    /** Call the GET API when the page is firstly loaded.
     *  After receiving a successful response, save the server data and format the date.
     */
    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'guideline',
            type: JSON.stringify(paginationModel),
            getAccessTokenSilently,
            email: user.email,
            onInit: () => setInitialLoading(true),
            onSuccess: (resp) => {
                console.log('resp.data.il_guideline', resp);
                setGuideLineRow(resp.data.il_guideline);
                setRowCount(resp.data.total);
                setInitialLoading(false);
            },
            onFinal: () => setInitialLoading(false),
        });
    }, [user.email, rowUpdateTrigger, paginationModel]);

    /** Checks if the current user has permission to modify (edit/delete) a specific row. */
    const hasPermissionForRow = (id) => {
        const canOpenDirectly =
            isAssistantManagerOrAbove(userRole);

        const canOpenAsStaff =
            userRole.includes('Operation-Staff') &&
            guideLineRow.some((item) => item.id === id && item.email === user.email);

        return canOpenDirectly || canOpenAsStaff;
    };

    /** Handler for opening the edit dialog and saving the row ID */
    const handleEdit = (id) => {
        if (hasPermissionForRow(id)) {
            setNewOpen(true);
            setSelectedRowId(id);
            return;
        }
        dispatch(
            setSnackbar({
                open: true,
                message: 'You do not have permission to edit the content.',
                severity: 'warning',
            })
        );
    };

    /** Handler for deleting a single row from the data grid and triggering a GET API request. */
    const handleDelete = (id) => {
        if (!hasPermissionForRow(id)) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'You do not have permission to delete the content.',
                    severity: 'warning',
                })
            );
            return;
        }

        if (window.confirm('Once deleted, the file cannot be recovered. Do you want to proceed?')) {
            callAPI({
                method: 'delete',
                module: 'guideline',
                getAccessTokenSilently,
                email: user.email,
                obj: id,
                onInit: () => setActionLoading(true),
                onSuccess: (resp) => {
                    setRowUpdateTrigger((prev) => !prev);
                    dispatch(setSnackbar({ open: true, message: 'Successfully Deleted.', severity: 'success' }));
                },
                onError: () =>
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Someting went wrong. Please try again.',
                            severity: 'error',
                        })
                    ),
                onFinal: () => setActionLoading(false),
            });
        }
    };

    /** Handler for downloading an uploaded file that is a zip file. */
    const handleDownload = (row) => {
        callAPI({
            method: 'get',
            module: 'guideline',
            component: row.id,
            type: 'blob',
            getAccessTokenSilently,
            email: user.email,
            responseType: 'blob',
            onInit: () => setActionLoading(true),
            onSuccess: (resp) => DownloadBlobAsZip(row.title, resp.data),
            onFinal: () => setActionLoading(false),
        });
    };

    /** Function for the detail panel to display the content of the guide.  */
    const getDetailPanelHeight = useCallback(() => 'auto', []);
    const getDetailPanelContent = useCallback(({ row }) => <GuidelineDetailPanel row={row} />, []);

    return (
        <>
            <Paper sx={customized_style.itemPaper}>
                <CircularLoading loading={actionLoading} />
                <PageHeader
                    title={'GUIDES & FAQs'}
                    icon={QuizIcon}
                    children={
                        <Box sx={{ ml: 'auto' }}>
                            <ThemeButton
                                role="New"
                                text="New"
                                icon={<AddIcon />}
                                onClick={() => setNewOpen(true)}
                                sx={{ px: '7px' }}
                            />
                        </Box>
                    }
                    sx={{ mb: 0 }}
                />
                {initialLoading ? (
                    <CustomSkeleton2 />
                ) : (
                    <MainDatagrid
                        rows={guideLineRow}
                        cols={getGuidelineCol(handleDownload, handleEdit, handleDelete)}
                        id_status={false}
                        showToolbar="CustomToolbar"
                        additional={{
                            loading: initialLoading,
                            getDetailPanelContent: getDetailPanelContent,
                            getDetailPanelHeight: getDetailPanelHeight,
                            rowCount: rowCount,
                            pageSizeOptions: [25, 50, 100],
                            paginationModel: paginationModel,
                            paginationMode: 'server',
                            onPaginationModelChange: setPaginationModel,
                            disableColumnMenu: true,
                        }}
                        sx={{ height: '84vh', fontSize: 12 }}
                    />
                )}
                <GuidelineDialog
                    dispatch={dispatch}
                    newOpen={newOpen}
                    setNewOpen={setNewOpen}
                    guideLineRow={guideLineRow}
                    setGuideLineRow={setGuideLineRow}
                    selectedRowId={selectedRowId}
                    setSelectedRowId={setSelectedRowId}
                    setRowUpdateTrigger={setRowUpdateTrigger}
                    setActionLoading={setActionLoading}
                />
                <CustomSnackbar />
            </Paper>
        </>
    );
}
